import capitalize from "lodash/capitalize";

import { shortMonthAndDay, shortMonthAndYear } from "@similarweb/pro-logic-components";
import type { ILegendItem } from "@similarweb/pro-logic-components/dist/graphs/components/GenericChartLegend/types";
import type { ILegendProps } from "@similarweb/ui-components";

import { GraphDurationToAmountAndUnit } from "app/common/keyword-research/clicks-over-time-graph/graphConsts";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import {
    GRAPH_API_URL,
    baseMetricItemsConfig,
} from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import type {
    IBreakdown,
    ITrendOverTimeResponseData,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import { GraphSourceTypeView, MetricNames } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { BasicDurations, customRangeFormat } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { webSourceToDeviceForApi } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { KeywordAnalysisGraphDashboardWidgetFilters } from "../widget-filters/KeywordAnalysisGraphDashboardWidgetFilters";
import { isValidUuidForKeywordGroup } from "../widget-utilities/isValidUuidForKeywordGroup";

import { GraphWidget } from "./GraphWidget";
import { createLegendObject, GraphKeywordAnalysisLegends } from "./KeywordAnalysisGraphDashboardWidget";

type KeyOrValue = "Key" | "Value";

type KeyOrValueRecord = Record<KeyOrValue, number>;

interface AggregatedSourceTypeData {
    Clicks: KeyOrValueRecord[];
    Volume: KeyOrValueRecord[];
}

interface SourceTypeBreakdownData {
    Organic: KeyOrValueRecord[];
    Paid: KeyOrValueRecord[];
    Volume: KeyOrValueRecord[];
}

export type DataSplitByMetric = AggregatedSourceTypeData | SourceTypeBreakdownData;

function createDataFetcherFactory(widget) {
    return {
        fetch: async (apiParams = widget.apiParams) => {
            const { duration, graphDuration, webSource, keys, ...restApiParams } = apiParams;

            const _params = {
                ...restApiParams,
                device: webSourceToDeviceForApi[webSource],
                duration: graphDuration,
                key: keywordGroupsTransforms.getGroupIdFromUrlParam(keys),
            };
            return DefaultFetchService.getInstance().get<ITrendOverTimeResponseData>(GRAPH_API_URL, _params);
        },
    };
}

export class KeywordAnalysisTrendOverTimeGraphWidget extends GraphWidget {
    reactLegendComponent: (props: ILegendProps) => React.ReactNode;

    constructor() {
        super();
        this.reactLegendComponent = GraphKeywordAnalysisLegends;
    }

    public isPptSupported = () => false;

    static getWidgetMetadataType() {
        return "KeywordAnalysisTrendOverTimeGraph";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    private dataFetcherFactory = () => {
        return {
            create: createDataFetcherFactory,
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    public validateData(response: any) {
        return Object.values(response).some((innerData) => Object.values(innerData).map((data) => data[1]));
    }

    public async onNewDataReceived(response: ITrendOverTimeResponseData) {
        const transformedData = this.transformData(response);
        await super.onNewDataReceived(transformedData);
    }

    public callbackOnGetData(response: any) {
        return super.callbackOnGetData(response, Object.keys(response.Data));
    }

    static getFiltersComponent() {
        return KeywordAnalysisGraphDashboardWidgetFilters;
    }

    getLegendItems(data?: ILegendItem[]) {
        return data && createLegendObject(data);
    }

    protected async getChartSeries(unorderedData: any, compareItemsKeys?: string[]): Promise<any[]> {
        const showSourceTypeBreakdown = this.apiParams.graphSourceType === GraphSourceTypeView.BREAKDOWN;
        const baseItemsConfig = baseMetricItemsConfig(showSourceTypeBreakdown);
        const chartSeries = await super.getChartSeries(unorderedData, compareItemsKeys);
        if (showSourceTypeBreakdown) {
            return chartSeries.map((item) => {
                const baseItem = baseItemsConfig.find((config) => config.id === item.seriesName);
                const updatedItem = {
                    ...item,
                    color: baseItem.color,
                    name: translate(baseItem.name),
                    type: baseItem.type,
                };
                if (item.seriesName === MetricNames.VOLUME) {
                    updatedItem.zIndex = baseItem.zIndex;
                }
                return updatedItem;
            });
        }
        return chartSeries;
    }

    getHighChartsConfig(chartOptions, formattedData, data) {
        const updatedChartOptions = {
            ...chartOptions,
            showLegend: false,
            tooltipFormatFunc: (val) => abbrNumberFilter()(val),
            xAxis: {
                tickPositioner: function () {
                    if (this.chart.series.length > 30) {
                        return this.chart.series.filter((_, index) => {
                            return index % 10 == 0;
                        });
                    }
                    if (this.chart.series.length > 8) {
                        return this.chart.series.filter((_, index) => {
                            return index % 3 == 0;
                        });
                    }
                    if (this.chart.series.length > 6) {
                        return this.chart.series.filter((_, index) => {
                            return index % 2 == 0;
                        });
                    }
                    return this.chart.series;
                },
            },
        };

        return data && super.getHighChartsConfig(updatedChartOptions, formattedData, data);
    }

    getGraphDuration = () => {
        const is28Days =
            this.apiParams.graphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS || this.durationObject?.raw.isWindow;

        const { to } = this.durationObject?.raw ?? {};
        const [amount, unit] = GraphDurationToAmountAndUnit[this.apiParams.graphDuration];
        if (is28Days) {
            return translate("keywordanalysis.organic2.clicks.over.time.duration.text", {
                duration: `${amount} ${capitalize(unit)}`,
                lastDate: to.format(shortMonthAndDay),
            });
        }

        return `${to.subtract(amount - 1, unit).format(shortMonthAndYear)} -  ${to.format(shortMonthAndYear)}`;
    };

    getViewData() {
        super.getViewData();
        const webSource = this._widgetConfig.properties.webSource;
        if (!this._widgetConfig.properties.country && !webSource) {
            return this.viewData;
        }
        this.viewData.duration = this.getGraphDuration();
        this.viewData.customSubtitle = this.apiParams.timeGranularity;
        return this.viewData;
    }

    public getProUrl() {
        const {
            country,
            webSource,
            isWindow,
            keys,
            graphDuration,
            timeGranularity,
            graphSourceType = GraphSourceTypeView.TOTAL,
        } = this.apiParams;

        const isKeywordGroup = isValidUuidForKeywordGroup(keys);

        const selectedMonth = this.durationObject.raw.to.format(customRangeFormat);
        const toParams = {
            country,
            duration: isWindow ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : `${selectedMonth}-${selectedMonth}`,
            webSource,
            keyword: isKeywordGroup ? `*${keys}` : keys,
            graphDuration,
            graphGranularity: timeGranularity,
            graphSourceType,
        };
        return proNavigator.getStateUrl(this._metricConfig.state, {
            ...toParams,
        });
    }

    private getDataSplitByMetric(Breakdown: IBreakdown, showSourceTypeBreakdown: boolean): DataSplitByMetric {
        return Object.entries(Breakdown).reduce(
            (acc, [date, data]) => {
                const {
                    Clicks: { Organic, Paid },
                    Volume,
                } = data;

                if (showSourceTypeBreakdown) {
                    return {
                        Volume: [...acc.Volume, { Key: date, Value: Volume }],
                        Organic: [...acc.Organic, { Key: date, Value: Organic }],
                        Paid: [...acc.Paid, { Key: date, Value: Paid }],
                    };
                } else {
                    return {
                        Volume: [...acc.Volume, { Key: date, Value: Volume }],
                        Clicks: [...acc.Clicks, { Key: date, Value: Organic + Paid }],
                    };
                }
            },
            { Clicks: [], Volume: [], Organic: [], Paid: [] } as any,
        );
    }

    private transformData(response: ITrendOverTimeResponseData) {
        if (!response) {
            return;
        }
        const { Breakdown } = response;
        const showSourceTypeBreakdown = this.apiParams.graphSourceType === GraphSourceTypeView.BREAKDOWN;
        const dataSplitByMetric = this.getDataSplitByMetric(Breakdown, showSourceTypeBreakdown);

        const transformedData = Object.entries(dataSplitByMetric).reduce(
            (acc, [key, data]) => ({ ...acc, [key]: { [key]: data } }),
            {},
        );

        return { Data: transformedData };
    }
}

KeywordAnalysisTrendOverTimeGraphWidget.register();
